import { Box, Loader } from "@mantine/core";
import { calculateAgeForTest, getNormalValues, parseDateOnly } from "../../../utils/CommonUtil";
import { round, sum } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import TestEntry from "../TestEntry";
import { VFABehaviour } from "types/tests";
import { getTestList } from "../../../utils/api-communication/RestApiCli";
import neighbours from "./neighbours.json";
import normalValues from "../../../data/normal_values.json";
import test_entry_config from "../config/test_entry_config.json";
import { useTranslation } from "react-i18next";

export function FilteredWrappedTestEntryForResult({}) {
  const [searchParams] = useSearchParams();

  const params = useMemo(
    () => ({
      activeTestId: searchParams.get("uuid"),
      filterNo: searchParams.get("no") ?? "1",
      boostFactorNo: searchParams.get("by") ?? "1",
    }),
    [searchParams],
  );

  return (
    <FilteredWrappedTestEntry
      activeTestId={params.activeTestId}
      filterNo={params.filterNo}
      boostFactorNo={params.boostFactorNo}
    />
  );
}

interface FilteredWrappedTestEntryProps {
  activeTestId: string;
  filterNo: string;
  boostFactorNo: string;
  onLoadComplete?: () => void;
}

export function FilteredWrappedTestEntry({
  activeTestId,
  filterNo,
  boostFactorNo,
  onLoadComplete,
  ...props
}: FilteredWrappedTestEntryProps) {
  const [testEntry, setTestEntry] = useState(null);
  const [resultsNotFound, setResultsNotFound] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);

  const handlePatientChanged = useCallback(() => {
    setRefetchKey((k) => k + 1);
  }, []);

  const navigate = useNavigate();
  const { t } = useTranslation();

  let boostVal;
  let notCheckBorders = true;
  const filters = [
    (testObj) => testObj,
    boostResult,
    addGaussianFilterVersion3,
    addGaussianFilterVersion4,
    addGaussianFilterVersion5,
    boostResultEqual,
    // Keep legacy filter-7 links working. The backend now owns this adjustment
    // in computedMetrics; this slot must leave recorded points untouched.
    (testObj) => testObj,
  ];

  useEffect(() => {
    if (activeTestId) {
      applyFilter(activeTestId, filterNo);
      boostVal = boostFactorNo;
    } else {
      boostNormalValues();
      onLoadComplete?.();
    }
  }, [refetchKey, activeTestId, filterNo, boostFactorNo, onLoadComplete, navigate]);

  const applyFilter = useCallback(
    (testId: string, fNo: string) => {
      if (testId && testId !== "") {
        const retryDelays = [3000, 3000, 3000];
        let currentRetry = 0;

        const fetchTestData = () => {
          getTestList(
            { uuid: testId },
            (response) => {
              const rawItem =
                response.data?.data && response.data.data.length > 0 ? response.data.data[0] : null;
              const item = rawItem ? { ...rawItem, orgScope: response.data?.orgScope } : null;

              if (!item && currentRetry < retryDelays.length) {
                const delay = retryDelays[currentRetry];
                currentRetry++;
                console.log(
                  `Retry attempt ${currentRetry} of ${retryDelays.length} after ${delay}ms`,
                );
                setTimeout(fetchTestData, delay);
                return;
              }

              if (!item && currentRetry >= retryDelays.length) {
                setResultsNotFound(true);
                onLoadComplete?.();
                return;
              }

              let n = item;
              if (item && Number(item?.testBehaviour) < Number(VFABehaviour.SIMPLE_TEST)) {
                n = filters[Number(fNo) - 1](item);
              }
              setTestEntry(n);
              onLoadComplete?.();
            },
            (error) => {
              if (currentRetry < retryDelays.length) {
                const delay = retryDelays[currentRetry];
                currentRetry++;
                console.log(
                  `Error occurred. Retry attempt ${currentRetry} of ${retryDelays.length} after ${delay}ms`,
                  error,
                );
                setTimeout(fetchTestData, delay);
              } else {
                console.error("Max retries reached, could not fetch test data", error);
                setResultsNotFound(true);
                onLoadComplete?.();
              }
            },
            () => navigate("/login"),
          );
        };

        fetchTestData();
      } else {
        onLoadComplete?.();
      }
    },
    [filters, navigate, onLoadComplete],
  );

  function addGaussianFilterVersion3(testObj) {
    const points = testObj.points;
    const neighs = neighbours[testObj.testType][testObj.eye];
    console.log("dates", testObj.startDate, testObj.patient.dob);
    const ageRange = (
      Math.floor(
        calculateAgeForTest(new Date(testObj.startDate), parseDateOnly(testObj.patient.dob)) / 5,
      ) * 5
    ).toString();
    const blindSpotIndex = testObj.eye === "rightEye" ? 34 : 28;
    const norms = getNormalValues({
      testType: testObj.testType,
      eye: testObj.eye,
      ageRange: ageRange,
    });
    console.log("blindSpotIndex", blindSpotIndex, ageRange, norms);
    for (let i = 0; i < neighs.length; i++) {
      if (i === blindSpotIndex) {
        continue;
      }
      const p = points[i];
      const ns = neighs[i];
      const fn = ns.firstOrderNeighbours;
      const sn = ns.secondOrderNeighbours;
      const fnTotDb = sum(fn.map((fnIndex) => norms[fnIndex] - points[fnIndex]));
      const snTotDb = sum(sn.map((snIndex) => norms[snIndex] - points[snIndex]));
      const totaldB = (norms[i] - p) * 4 + fnTotDb * 2 + snTotDb;
      let result = norms[i] - round(totaldB / (4 + fn.length * 2 + sn.length));
      if (result < 0) {
        result = 0;
      }
      points[i] = result;
    }
    testObj.points = points;
    return testObj;
  }

  function addGaussianFilterVersion4(testObj) {
    const points = testObj.points;
    const neighs = neighbours[testObj.testType][testObj.eye];
    console.log("dates", testObj.startDate, testObj.patient.dob);
    const ageRange = (
      Math.floor(
        calculateAgeForTest(new Date(testObj.startDate), parseDateOnly(testObj.patient.dob)) / 5,
      ) * 5
    ).toString();
    const blindSpotIndex = testObj.eye === "rightEye" ? 34 : 28;
    const norms = getNormalValues({
      testType: testObj.testType,
      eye: testObj.eye,
      ageRange: ageRange,
    });
    const newPoints = {};
    console.log("blindSpotIndex", blindSpotIndex, ageRange);
    for (let i = 0; i < neighs.length; i++) {
      if (i === blindSpotIndex) {
        continue;
      }
      const p = points[i];
      const ns = neighs[i];
      const fn = ns.firstOrderNeighbours;
      const sn = ns.secondOrderNeighbours;
      const fnTotDb = sum(fn.map((fnIndex) => norms[fnIndex] - points[fnIndex]));
      const snTotDb = sum(sn.map((snIndex) => norms[snIndex] - points[snIndex]));
      const totaldB = (norms[i] - p) * 4 + fnTotDb * 2 + snTotDb;
      let result = norms[i] - round(totaldB / (4 + fn.length * 2 + sn.length));
      if (result < 0) {
        result = 0;
      }
      newPoints[i] = result;
    }
    newPoints[blindSpotIndex] = points[blindSpotIndex];
    testObj.points = newPoints;
    return testObj;
  }

  function addGaussianFilterVersion5(testObj) {
    const points = testObj.points;
    const neighs = neighbours[testObj.testType][testObj.eye];
    console.log("dates", testObj.startDate, testObj.patient.dob);
    const ageRange = (
      Math.floor(
        calculateAgeForTest(new Date(testObj.startDate), parseDateOnly(testObj.patient.dob)) / 5,
      ) * 5
    ).toString();
    const blindSpotIndex = testObj.eye === "rightEye" ? 34 : 28;
    const norms = getNormalValues({
      testType: testObj.testType,
      eye: testObj.eye,
      ageRange: ageRange,
    });
    console.log("blindSpotIndex- 5", blindSpotIndex, ageRange, norms);
    for (let i = 0; i < neighs.length; i++) {
      if (i === blindSpotIndex) {
        continue;
      }
      const p = points[i];
      const ns = neighs[i];
      const fn = ns.firstOrderNeighbours;
      const sn = ns.secondOrderNeighbours;
      const fnTotDb = sum(fn.map((fnIndex) => norms[fnIndex] - points[fnIndex]));
      const snTotDb = sum(sn.map((snIndex) => norms[snIndex] - points[snIndex]));
      const totaldB = (norms[i] - p) * 4 + fnTotDb * 2 + snTotDb;
      const result = norms[i] - round(totaldB / (4 + fn.length * 2 + sn.length));
      if ((points[i] === 0 && result > 5) || result < 0) {
        continue;
      }
      points[i] = result;
    }
    testObj.points = points;
    return testObj;
  }

  function boostResultEqual(testObj) {
    console.log("boostResultEqual");
    Object.keys(testObj.points).forEach((key) => {
      const orig = parseInt(testObj.points[key]);
      if (orig > 4 && orig < 30) testObj.points[key] = orig + parseInt(boostFactorNo);
    });
    return testObj;
  }

  function arrayOfNs(n, len) {
    return Array.from(Array(len), () => n);
  }

  function boostNormalValues() {
    boostVal = -1;
    notCheckBorders = true;
    const newNormals = normalValues;
    const norms = normalValues["30-2"]["rightEye"][0];
    Object.keys(normalValues).forEach((testType) => {
      if (testType === "30-2") {
        Object.keys(normalValues[testType]).forEach((eye) => {
          Object.keys(normalValues[testType][eye]).forEach((age) => {
            if (age !== "original_values")
              newNormals[testType][eye][age] = boostTestPoints(
                normalValues[testType][eye][age],
                testType,
                eye,
              );
          });
        });
      }
    });
    console.log("newNormals", norms, newNormals, JSON.stringify(newNormals));
  }

  function boostTestPoints(arr, testType, eye) {
    const elementsInRow = test_entry_config["thresholdMap"][testType][eye]["elementsInRow"];
    const { front: padFrontAmountAtEachRow, back: padBackAmountAtEachRow } = buildSquareRowPadding(
      elementsInRow,
      eye,
    );

    const beforeCalculation = [];

    for (let i = 0; i < elementsInRow.length; i++) {
      const tmp = arr.splice(0, elementsInRow[i]);
      beforeCalculation.push(
        arrayOfNs(-1, padFrontAmountAtEachRow[i])
          .concat(tmp)
          .concat(arrayOfNs(-1, padBackAmountAtEachRow[i])),
      );
    }

    const xLength = beforeCalculation[0].length;
    let padded = [arrayOfNs(-1, xLength)].concat(beforeCalculation, [arrayOfNs(-1, xLength)]);

    console.log(padded);

    for (let i = 0; i < padded.length / 2; i++) {
      padded = boostSquare(padded, i, boostVal * (padded.length / 2 - i), notCheckBorders);
      console.log("padded", padded);
    }

    let pointsArr = [];
    for (let i = 0; i < elementsInRow.length + 1; i++) {
      pointsArr = pointsArr.concat(
        padded[i + 1].slice(
          padFrontAmountAtEachRow[i],
          padFrontAmountAtEachRow[i] + elementsInRow[i],
        ),
      );
    }

    console.log("pointsArr", pointsArr);
    return pointsArr;
  }

  function boostResult(testObj) {
    const arr = [];
    Object.keys(testObj.points).forEach((key) => arr.push(testObj.points[key]));
    const newPoints = boostTestPoints(arr, testObj.testType, testObj.eye);

    Object.keys(testObj.points).forEach((key) => {
      return (testObj.points[key] = newPoints[parseInt(key)]);
    });
    return testObj;
  }

  function boostSquare(padded, borderIndex, inc, filter) {
    const toIncrementLen = (padded.length / 2 - borderIndex) * 2;
    const index2 = borderIndex + toIncrementLen - 1;

    if (filter || !(padded[borderIndex][borderIndex] < 9 || padded[borderIndex][borderIndex] > 34))
      padded[borderIndex][borderIndex] += inc;
    for (let i = borderIndex + 1; i <= index2; i++) {
      if (filter || !(padded[borderIndex][i] < 9 || padded[borderIndex][i] > 34))
        padded[borderIndex][i] += inc;
      if (filter || !(padded[i][borderIndex] < 9 || padded[i][borderIndex] > 34))
        padded[i][borderIndex] += inc;
    }
    if (filter || !(padded[index2][index2] < 9 || padded[index2][index2] > 34))
      padded[index2][index2] += inc;
    for (let i = index2 - 1; i >= borderIndex + 1; i--) {
      if (filter || !(padded[index2][i] < 9 || padded[index2][i] > 34)) padded[index2][i] += inc;
      if (filter || !(padded[i][index2] < 9 || padded[i][index2] > 34)) padded[i][index2] += inc;
    }
    return padded;
  }

  if (testEntry) {
    return (
      <TestEntry
        entry={testEntry}
        goBack={() => navigate("/list")}
        onPatientChanged={handlePatientChanged}
        {...props}
      />
    );
  }

  if (resultsNotFound) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p style={{ fontSize: "1.2rem", color: "var(--mantine-color-dimmed)" }}>
          {t("TestController.noTests")}
        </p>
      </div>
    );
  }

  if (activeTestId) {
    return (
      <Box
        className="no-print"
        py="xl"
        style={{ display: "flex", justifyContent: "center" }}
        data-testid="test-result-loading"
      >
        <Loader size="lg" aria-label={t("TestController.loadingResult")} />
      </Box>
    );
  }

  return null;
}

/**
 * Row padding that squares off a test pattern, for the ring boost below.
 *
 * The width has to be even so the vertical meridian falls between two columns
 * rather than through one, which leaves a spare column on rows with an odd
 * count. Which side it lands on is the one thing that differs between the eyes.
 */
export function buildSquareRowPadding(
  elementsInRow: number[],
  eye: string,
): { front: number[]; back: number[] } {
  const widest = Math.max(...elementsInRow);
  const width = widest + (widest % 2);
  const spareColumnGoesAfterRow = eye === "rightEye";

  const front = elementsInRow.map((count) => {
    const slack = width - count;
    return spareColumnGoesAfterRow ? Math.floor(slack / 2) : Math.ceil(slack / 2);
  });

  return { front, back: elementsInRow.map((count, row) => width - count - front[row]) };
}
